import { MULTI_SELECT_TEMPORARY_ID } from '@/editor/constant/entity';
import { EWallFaceLayout } from '@/editor/data/face/face';
import { ESeparateWallType } from '@/editor/data/separateWall/SeparateWall';
import { EFaceType } from '@/editor/entities/type';
import { findEntityInfo } from '@/editor/entities/utils';
import { TGlobalStores } from '@/editor/stores';
import { EEntityType, EFaceSide, ISelected } from '@/editor/types';
import { calMousePosition } from '@/editor/utils';
import * as THREE from 'three';
import { isInMultiModel } from '../utils';

interface IRayEntity3dOptions {
  resolveMultiModel?: boolean;
}

export function rayEntity3d(
  gs: TGlobalStores,
  event: MouseEvent,
  raycaster: THREE.Raycaster,
  mouse: THREE.Vector2,
  options: IRayEntity3dOptions = {}
): ISelected {
  const { resolveMultiModel = true } = options;
  const emptyEntityInfo = { entityId: '', type: EEntityType.EMPTY };
  const {
    canvasElement,
    scene3d: { camera, intersectableGroup }
  } = gs.editorStateStore.sceneInfo;

  mouse.copy(calMousePosition(event, canvasElement));
  raycaster.setFromCamera(mouse, camera);

  const intersects = raycaster.intersectObject(intersectableGroup, true);
  if (!intersects.length) return emptyEntityInfo;

  const intersectedEntityInfos = intersects
    .map((item): ISelected | null => {
      const entityInfo = findEntityInfo(item.object);
      return entityInfo
        ? { entityId: entityInfo.entityId, type: entityInfo.type, intersectionPoint3d: item.point }
        : null;
    })
    .filter((entityInfo): entityInfo is ISelected => {
      if (!entityInfo) return false;
      if (entityInfo.type !== EEntityType.Face) return true;

      const faceData = gs.faceStore.getFaceData(entityInfo.entityId);
      if (!faceData) return false;

      // 墙体侧面可选
      if (
        faceData.type === EFaceType.WallFace &&
        (faceData.relativeInfo.side === EFaceSide.MainLeftSide ||
          faceData.relativeInfo.side === EFaceSide.MainRightSide ||
          faceData.relativeInfo.side === EFaceSide.StartSide ||
          faceData.relativeInfo.side === EFaceSide.EndSide)
      ) {
        return true;
      }

      // 玻璃隔墙不可选
      if (faceData.relativeInfo.sepType === ESeparateWallType.GlassWall) return false;

      // 半玻隔墙玻璃段不可选
      if (
        faceData.relativeInfo.sepType === ESeparateWallType.HalfGlassWall &&
        faceData.relativeInfo.layout === EWallFaceLayout.MIDDLE
      )
        return false;

      // 分隔墙体侧面可选
      if (
        faceData.type === EFaceType.SepWallFace &&
        (faceData.relativeInfo.sepSide === EFaceSide.MainLeftSide ||
          faceData.relativeInfo.sepSide === EFaceSide.MainRightSide ||
          faceData.relativeInfo.sepSide === EFaceSide.StartSide ||
          faceData.relativeInfo.sepSide === EFaceSide.EndSide ||
          faceData.relativeInfo.sepSide === EFaceSide.Single)
      ) {
        return true;
      }

      // 柱子侧面可选
      if (
        faceData.type === EFaceType.ColumnFace &&
        faceData.relativeInfo.columnSide !== EFaceSide.TopSide &&
        faceData.relativeInfo.columnSide !== EFaceSide.BottomSide
      ) {
        return true;
      }

      return false;
    });

  if (!intersectedEntityInfos.length) return emptyEntityInfo;

  const intersectedEntityInfo = intersectedEntityInfos[0];
  if (resolveMultiModel && isInMultiModel(intersectedEntityInfo)) {
    return {
      entityId: MULTI_SELECT_TEMPORARY_ID,
      type: EEntityType.MultiModel,
      intersectionPoint3d: intersectedEntityInfo.intersectionPoint3d
    };
  }

  return intersectedEntityInfo;
}
